import { InlineEditorLogic, isEditMode, useInlineEditor } from '@tableau/vizportalclient-common-components';
import React from 'react';
import { connect } from 'react-redux';
import _ from 'underscore';

import { selectSessionSite } from '~selectors/SessionInfoSelectors';
import { AppState } from '~src/app/state/AppState';
import { MapStateToProps } from '~src/app/utils/ConnectUtils';
import { Messages } from '~src/codegen/Localize';
import { AskDataIndexing, IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { rejectIfResultContainsError } from '~src/httpClient/utils/HttpErrorChecker';
import { AskDataMode } from '~src/settings/types/AskDataMode';
import { ACTION_DIALOG_ID } from '~src/spacesPlatform/components/content/contentDetailDialog/ContentDetailsDialog';
import {
  ContentDetailSection,
  DetailEditButton,
  DetailSaveCancelButtons
} from '~src/spacesPlatform/components/content/contentDetailDialog/ContentDetailSection';
import { selectSpaceInfoItem } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';

import { AskDataEditMode } from './AskDataEditMode';
import { AskDataReadingMode } from './AskDataReadingMode';
import { AskDataSettingsEditorState } from './AskDataSettingsEditorState';


// ID for accessibilty purposes
const sectionHeaderId = ACTION_DIALOG_ID + '-AskData';

interface AskDataSettingsSectionOwnProps {
  readonly isAdminOrOwner: boolean;
  readonly setAskDataSettings: (indexing: AskDataIndexing) => Promise<IResult>;
  readonly incrementRouterSpaceReactKey: () => void;
}

interface AskDataSettingsSectionStateProps {
  readonly askDataMode: AskDataMode;
  readonly askDataIndexing: AskDataIndexing;
}

export type AskDataSettingsProps = AskDataSettingsSectionOwnProps & AskDataSettingsSectionStateProps;

export function AskDataSettingsSection(props: AskDataSettingsProps): JSX.Element {
  const onSave = React.useCallback(
    async (editorState: AskDataSettingsEditorState) => {
      await props.setAskDataSettings(editorState.askDataIndexing)
        .then(rejectIfResultContainsError)
        .then(props.incrementRouterSpaceReactKey);
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.askDataIndexing,
      props.setAskDataSettings,
      props.incrementRouterSpaceReactKey,
    ]
  );

  const canSave = (editorState: AskDataSettingsEditorState) => {
    return !_.isEqual(editorState, initialState);
  };

  const initialState: AskDataSettingsEditorState = {
    askDataIndexing: props.askDataIndexing,
  };

  const logic: InlineEditorLogic<AskDataSettingsEditorState> = useInlineEditor<AskDataSettingsEditorState>({
    editorStateWhenEnteringEditMode: initialState,
    canEdit: props.isAdminOrOwner,
    onSave: onSave,
    canSaveFn: canSave,
  });

  if (props.askDataMode === AskDataMode.DISABLED_ALWAYS) {
    return <></>;
  }

  if (isEditMode(logic)) {
    return (
      <ContentDetailSection
        header={Messages.nlp()}
        testId={'ask-data'}
        headerId={sectionHeaderId}
        detail={
          <AskDataEditMode
            askDataMode={props.askDataMode}
            askDataIndexing={logic.editorState.askDataIndexing}
            setEditorState={logic.setEditorStateWithinEditMode}
            disableControls={logic.isActionBusy}
          />
        }
        isActionAllowed={true}
        action={<DetailSaveCancelButtons inlineEditorLogic={logic} />}
      />
    );
  }
  else {
    return (
      <ContentDetailSection
        header={Messages.nlp()}
        testId={'ask-data'}
        detail={<AskDataReadingMode askDataIndexing={props.askDataIndexing} />}
        isActionAllowed={logic.canEdit}
        action={
          <DetailEditButton
            buttonRef={logic.editButtonRef}
            buttonLabel={Messages.dataSourceNlpSetting_editNlpSettingLabel()}
            buttonTestId={'edit-ask-data'}
            onClickAction={logic.enterEditMode}
          />
        }
      />
    );
  }
}

const mapStateToProps: MapStateToProps<AskDataSettingsSectionStateProps> = (state: AppState) => ({
  askDataMode: selectSessionSite(state).askDataMode,
  askDataIndexing: (selectSpaceInfoItem(state) as IDataSourceInfo).askDataIndexing,
});

export const ConnectedAskDataSettingsSection: React.ComponentClass<AskDataSettingsSectionOwnProps> =
  connect(mapStateToProps)(AskDataSettingsSection);
